import React, { useEffect, useState } from "react";
import { FaEye, FaEyeSlash, FaSpinner } from "react-icons/fa";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { invitationService } from "../../../services"; // Make sure this is correctly imported
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";

const RegistrationForm = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    nric: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // New state for initial verification
  const [showForm, setShowForm] = useState(false); // Controls whether to show form
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useParams();

  useEffect(() => {
    const verifyTokenOnLoad = async () => {
      const path = location.pathname;
      const lastSegment = path.split("/").pop();

      if (lastSegment && lastSegment.includes("&&&")) {
        const [token, email] = lastSegment.split("&&&");
        const decodedEmail = decodeURIComponent(email);

        sessionStorage.setItem("invitationToken", token);
        sessionStorage.setItem("invitationEmail", decodedEmail);

        // Only verify if token is "add-user-only"
        if (token === "add-user-only") {
          try {
            const isValid = await verifyLink(token, decodedEmail);
            if (!isValid) {
              navigate("/expired");
              return;
            }
          } catch (error) {
            console.error("Token verification error:", error);
            navigate("/expired");
            return;
          }
        }
      }

      setInitialLoading(false);
      setShowForm(true);
    };

    verifyTokenOnLoad();
  }, [location, navigate]);

  if (!["invitation", "reassign"].includes(mode)) {
    return <Navigate to="/not-found" />;
  }

  const verifyLink = async (token, email) => {
    try {
      const response = await invitationService.verifyLink({
        Token: `${token}&&&${email}`,
      });
      return response.Data === "pass";
    } catch (error) {
      console.error("Link verification failed:", error);
      return false;
    }
  };

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    const token = sessionStorage.getItem("invitationToken");
    const email = sessionStorage.getItem("invitationEmail");

    if (!token || !email) {
      alert("Missing invitation token or email.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        Token: token,
        Email: email,
        FirstName: form.firstName,
        LastName: form.lastName,
        NRIC: form.nric,
        Phone: form.phone,
        Password: form.password,
      };

      let response;
      if (mode === "invitation") {
        response = await invitationService.invitationSignup(payload);
      } else if (mode === "reassign") {
        response = await invitationService.reassignSignup(payload);
      } else {
        throw new Error("Invalid mode");
      }

      console.log("Signup successful:", response);
      navigate(token === "add-user-only" ? "/invitesuccess" : `/${mode}/login`);
    } catch (error) {
      console.error("Signup failed:", error);
      alert("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show spinner during initial verification
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-teal-500 to-teal-400">
        <FaSpinner className="animate-spin text-white text-4xl" />
      </div>
    );
  }

  // Only show form after initial verification is complete
  if (!showForm) {
    return null;
  }
  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-teal-500 to-teal-400">
      <div className="text-center text-white pt-12 mb-6">
        <h1 className="text-4xl font-bold">Welcome Aboard!</h1>
        <p className="text-md">Fill in your details to join.</p>
      </div>

      <div className="absolute w-72 h-72 bg-white opacity-10 rounded-full top-10 left-10"></div>
      <div className="absolute w-96 h-96 bg-white opacity-10 rounded-full bottom-10 right-10"></div>
      <div className="absolute w-96 h-96 bg-white opacity-10 rounded-full top-12 center"></div>

      <div className="bg-white p-8 mb-12 rounded-2xl shadow-lg w-full z-10 max-w-md">
        <h2 className="text-center text-2xl font-semibold">New Account</h2>
        <p className="text-center text-gray-600 mb-4">
          Create a new account below <br />{" "}
          <span className="text-xs">
            Required fields are marked with{" "}
            <span className="text-red-600">*</span>
          </span>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <Label>
                First Name <span className="text-red-600">*</span>
              </Label>
              <Input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                placeholder="Enter First Name"
                required
              />
            </div>
            <div>
              <Label>
                Last Name <span className="text-red-600">*</span>
              </Label>
              <Input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                placeholder="Enter Last Name"
                required
              />
            </div>
            <div>
              <Label>
                NRIC{" "}
                <span className="text-xs text-gray-500">
                  (National Registration Identity Card)
                </span>
              </Label>
              <Input
                type="text"
                name="nric"
                value={form.nric}
                onChange={handleChange}
                placeholder="Enter NRIC"
              />
            </div>
            <div>
              <Label>Mobile Number</Label>
              <div>
                <Input
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Enter Mobile Number"
                />
              </div>
            </div>
            <div>
              <Label className="block text-gray-700 font-semibold">
                Password <span className="text-red-600">*</span>
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter Password"
                  className="w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </div>
            <div>
              <Label className="block text-gray-700 font-semibold">
                Confirm Password <span className="text-red-600">*</span>
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm Password"
                  className="w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500"
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;
